import { useMutation, useQueryClient } from "@tanstack/react-query";
import { sendChatMessage } from "../services/discuss.service";
import type { ChatMessage, ChatHistoryResponse } from "../types/discuss.types";
import { keepDisplayedTimestamp } from "../lib/utils";

/**
 * Fold the server's reply into the pending message.
 *
 * Replaces the message object rather than editing it in place. Spreading the
 * array copies the array but NOT the objects inside it, so the previous code's
 * `lastMessage.creation_date = ...` mutated the cached object directly — which
 * the query cache cannot detect as a change, so the screen kept showing stale
 * values until something unrelated forced a re-render.
 */
function applyServerReply(
  oldData: ChatHistoryResponse | undefined,
  question: string,
  data: { response: string; related_questions: string[]; creation_date: string }
): ChatHistoryResponse | undefined {
  if (oldData == null) return oldData;
  const lastIndex = oldData.prev_chat.length - 1;
  return {
    ...oldData,
    prev_chat: oldData.prev_chat.map((message, index) => {
      if (index !== lastIndex || message.question !== question) return message;
      return {
        ...message,
        response: data.response,
        related_questions: data.related_questions,
        creation_date: keepDisplayedTimestamp(message.creation_date, data.creation_date),
      };
    }),
  };
}

/**
 * Hook to send a chat message and get AI response
 */
export const useSendMessage = (documentId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: sendChatMessage,
    mutationKey: ["sendChatMessage", documentId],
    onMutate: async (newChatMessage) => {
      const previousChatHistory = queryClient.getQueryData<ChatHistoryResponse>([
        "chatHistory",
        documentId,
      ]);

      queryClient.setQueryData<ChatHistoryResponse>(["chatHistory", documentId], (oldData) => {
        if (oldData == null) return oldData;
        const newUserMessage: ChatMessage = {
          question: newChatMessage.data,
          response: "",
          creation_date: new Date().toISOString(),
          related_questions: [],
        };
        return {
          ...oldData,
          prev_chat: [...oldData.prev_chat, newUserMessage],
        };
      });

      return { previousChatHistory };
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData<ChatHistoryResponse>(["chatHistory", documentId], (oldData) =>
        applyServerReply(oldData, variables.data, data)
      );
    },
    onError: (error, _newChatMessage, context) => {
      console.error("Failed to send message:", error);
      // Roll back the optimistic turn. createSendMessageHandler restores the
      // draft and toasts; writing an error onto the restored snapshot was a
      // no-op because that turn is no longer in the cache.
      if (context?.previousChatHistory != null) {
        queryClient.setQueryData<ChatHistoryResponse>(
          ["chatHistory", documentId],
          context.previousChatHistory
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["chatHistory", documentId] });
    },
  });
};
